import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { App, Modal } from 'antd';
import { useBlocker, useLocation } from 'react-router-dom';

/**
 * The one unsaved-changes guard for in-app navigation.
 *
 * react-router's data router supports ONE blocker at a time (a second
 * `useBlocker` wins silently and warns), and a page can hold several dirty
 * forms at once — a card page with its form-layout modal open, a collection's
 * settings with an export definition being edited. So no form blocks on its
 * own: each `useDirtyForm` registers here, and this provider owns the single
 * `useBlocker`. A navigation is held when any registered form says it would
 * lose typing by it, and goes only once every such form is answered for: a
 * form's own leave prompt for itself, the shared confirm below (asked once)
 * for the rest.
 *
 * Mounted by the root route element (src/AppRoot.js), under the router and
 * inside the antd App (ThemeProvider), so the confirm is themed. A form
 * rendered without it — a component test under a plain MemoryRouter — still
 * tracks dirtiness, still guards `beforeunload`, and its Cancel still asks;
 * only link and back-button navigation go unguarded.
 */

const UnsavedChangesContext = createContext(null);

export const DISCARD_TITLE = 'Discard unsaved changes?';
const DISCARD_CONTENT = 'What you changed here has not been saved.';

/**
 * Every "Discard unsaved changes?" still on screen, by who asked: a form (its
 * `useDirtyForm`) or the router blocker. The dialog renders in the antd App
 * ABOVE the router, so nothing about the form that asked keeps it alive or
 * takes it down — this does. A request goes stale when its form unmounts
 * or a navigation completes (`dismissDiscardConfirms`); a stale dialog is
 * destroyed, its promise settles as Keep editing, and a click that reaches it
 * anyway is ignored (the token no longer matches). One asker has at most one
 * dialog open: asking again while it is up joins the open one.
 */
const openConfirms = new Map();

/**
 * Close the open confirms — the one `owner` asked for, or every one when no
 * owner is given — and settle them as Keep editing.
 */
export function dismissDiscardConfirms(owner) {
	const owners = typeof owner === 'undefined' ? [...openConfirms.keys()] : [owner];
	owners.forEach(key => {
		const open = openConfirms.get(key);
		if (!open) return;
		openConfirms.delete(key);
		open.instance?.destroy?.();
		open.resolve(false);
	});
}

/**
 * The shared confirm. Resolves true on Discard, false on Keep editing (and
 * on Esc, the ✕, or the mask — every way out of the dialog that is not the
 * red button keeps the typing — and when the request goes stale). The cancel
 * button takes focus, so Enter never drops work by accident.
 *
 * `modalApi` is antd's `App.useApp().modal` (themed); outside an antd App
 * the static `Modal` stands in. `options.owner` names the asker, so its
 * dialog can be dismissed with it (see `openConfirms`).
 */
export function askDiscardChanges(modalApi, options = {}) {
	const owner = options.owner || {};
	const already = openConfirms.get(owner);
	if (already) return already.promise;

	const api = modalApi && typeof modalApi.confirm === 'function' ? modalApi : Modal;
	const token = {};
	let resolve;
	const promise = new Promise(settle => { resolve = settle; });
	const open = { token, promise, resolve, instance: null };
	// Only the current request's dialog may answer.
	const answer = ok => {
		if (openConfirms.get(owner)?.token !== token) return;
		openConfirms.delete(owner);
		resolve(ok);
	};
	openConfirms.set(owner, open);
	open.instance = api.confirm({
		title: options.title || DISCARD_TITLE,
		content: options.content || DISCARD_CONTENT,
		okText: options.okText || 'Discard changes',
		okButtonProps: { danger: true },
		cancelText: 'Keep editing',
		autoFocusButton: 'cancel',
		onOk: () => answer(true),
		onCancel: () => answer(false),
	});
	return promise;
}

export function UnsavedChangesProvider({ children }) {
	const entriesRef = useRef(new Set());
	const { modal } = App.useApp();
	const modalRef = useRef(modal);
	modalRef.current = modal;
	const location = useLocation();
	const locationRef = useRef(location);
	locationRef.current = location;

	const register = useCallback((entry) => {
		entriesRef.current.add(entry);
		return () => { entriesRef.current.delete(entry); };
	}, []);

	const blockingEntries = useCallback((currentLocation, nextLocation) => (
		[...entriesRef.current].filter(entry => entry.blocks(currentLocation, nextLocation))
	), []);

	// Read at navigation time, never captured: the forms' dirtiness lives in
	// their own refs, so a save that marks the form pristine and navigates in
	// the same tick is let through.
	const shouldBlock = useCallback(({ currentLocation, nextLocation }) => (
		blockingEntries(currentLocation, nextLocation).length > 0
	), [blockingEntries]);

	const blocker = useBlocker(shouldBlock);
	const blockerRef = useRef(blocker);
	blockerRef.current = blocker;

	// A navigation that landed leaves every question asked on the page it
	// left stale — a Cancel's confirm from a form that is gone, above all.
	useEffect(() => { dismissDiscardConfirms(); }, [location.key]);

	// The blocker's own dialog, dismissed with the blocked navigation.
	const blockerOwnerRef = useRef({});

	useEffect(() => {
		if (blocker.state !== 'blocked') return undefined;
		let live = true;
		const owner = blockerOwnerRef.current;
		const current = blockerRef.current;
		const from = locationRef.current;
		const to = current.location;
		const proceed = () => {
			if (!live) return;
			live = false;
			if (blockerRef.current.state === 'blocked') blockerRef.current.proceed();
		};
		const reset = () => {
			if (!live) return;
			live = false;
			if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
		};

		// Every form that would lose typing is answered for, one at a time,
		// and the navigation goes only when all of them have said yes. A form
		// with a leave prompt of its own (the new-item form's Keep as draft /
		// Discard) answers for itself first; whatever is still dirty after
		// that gets the one shared question. Nothing is dropped unasked.
		const stillBlocking = entry => entriesRef.current.has(entry) && entry.blocks(from, to);
		const askCustom = entry => new Promise(settle => {
			let settled = false;
			const once = answer => { if (!settled) { settled = true; settle(answer); } };
			const took = entry.onBlocked({
				proceed: () => once(true),
				reset: () => once(false),
				location: to,
			});
			if (took !== true) once(null);
		});

		(async () => {
			const entries = blockingEntries(from, to);
			const generic = [];
			for (const entry of entries) {
				if (!live) return;
				if (!stillBlocking(entry)) continue;
				if (typeof entry.onBlocked !== 'function') {
					generic.push(entry);
					continue;
				}
				const answer = await askCustom(entry);
				if (!live) return;
				if (answer === false) { reset(); return; }
				if (answer === true) entry.accept();
				else generic.push(entry);
			}
			const rest = generic.filter(stillBlocking);
			if (rest.length) {
				const ok = await askDiscardChanges(modalRef.current, { owner });
				if (!live) return;
				if (!ok) { reset(); return; }
				rest.forEach(entry => entry.accept());
			}
			// Whatever held it has been saved, kept or discarded.
			proceed();
		})();

		return () => {
			live = false;
			dismissDiscardConfirms(owner);
		};
	}, [blocker.state, blockingEntries]);

	const value = useMemo(() => ({ register }), [register]);
	return (
		<UnsavedChangesContext.Provider value={value}>
			{children}
		</UnsavedChangesContext.Provider>
	);
}

/** The registry, or null outside the provider (see the header). */
export function useUnsavedChangesRegistry() {
	return useContext(UnsavedChangesContext);
}
